import {
  MatcherTableDataQuery,
  MatcherTableHeadersQuery,
  MatcherTablesQuery,
  TableData,
  useMatcherTableDataLazyQuery,
  useMatcherTableHeadersLazyQuery,
  useMatcherTablesQuery,
  useViewDataLazyQuery,
  useViewHeadersLazyQuery,
  useViewsQuery,
  ViewDataQuery,
  ViewHeadersQuery,
  ViewsData,
  ViewsQuery,
} from "graphql/types/graphql";
import { MatcherView, useMatcher as useMatcherContext } from "utils/MatcherProvider";
import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { enqueueSnackbar } from "notistack";
import { context } from "utils/Provider";
import { CurrentCube } from "@/utils/types";

interface MainViewMatcherService {
  views: ViewsQuery;
  matcherData: MatcherTablesQuery;
  selectedTables: {
    row1: any;
    row2: any;
  };
  isTableDisabled: (tableName: string, currentRow: any) => boolean;
  handleSelect: (tableName: string, row: "row1" | "row2") => void;
  areTablesSelected: any;
  selectedMatcherColumn: string;
  matcherTableData: MatcherTableDataQuery;
  identifier: string | null;
  matcherTableHeadersData: MatcherTableHeadersQuery;
  setIdentifier: (value: string) => void;
  columnPairs: {
    columnA: string;
    columnB: string;
  }[];
  handleColumnChange: (index: number, column: string, value: string) => void;
  viewHeadersData: ViewHeadersQuery;
  removeColumnPair: (index: number) => void;
  addColumnPair: () => void;
  isColumnSelected: boolean;
  handleMatchTables: () => void;
  selectedColumn: string;
  viewData: ViewDataQuery;
  matcherPageData: { page: number; pageSize: number; count: number };
  handleMatcherPageChange: (page: number) => void;
  handleMatcherPageSizeChange: (pageSize: number) => void;
  currentMatcherTables: TableData[];
  viewPageData: { page: number; pageSize: number; count: number };
  handleViewPageChange: (page: number) => void;
  handleViewPageSizeChange: (pageSize: number) => void;
  currentViewsTables: ViewsData[];
  selectedCube: CurrentCube;
}
export function useMainView(): MainViewMatcherService {
  const { selectedCompany, setIsLoading, selectedCube } = useContext(context);
  const { phaseData, setPhaseData, changeView } = useMatcherContext();
  const navigate = useNavigate();

  const [matcherPage, setMatcherPage] = useState(0);
  const [matcherPageSize, setMatcherPageSize] = useState(12);

  const [viewPage, setViewPage] = useState(0);
  const [viewPageSize, setViewPageSize] = useState(12);

  const { data, loading, error } = useViewsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        cubeId: selectedCube.cubeId,
      },
      limit: viewPageSize,
      offset: viewPage * viewPageSize,
    },
  });

  const {
    data: matcherData,
    loading: matcherLoading,
    error: matcherError,
  } = useMatcherTablesQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: matcherPageSize,
      offset: matcherPage * matcherPageSize,
      cubeId: selectedCube.cubeId,
    },
  });

  const [getViewHeaders, { data: viewHeadersData }] = useViewHeadersLazyQuery();
  const [getViewData, { data: viewData }] = useViewDataLazyQuery({ notifyOnNetworkStatusChange: true });
  const [getMatcherTableHeaders, { data: matcherTableHeadersData }] = useMatcherTableHeadersLazyQuery();
  const [getMatcherTableData, { data: matcherTableData }] = useMatcherTableDataLazyQuery();

  const [selectedTables, setSelectedTables] = useState({ row1: null, row2: null });
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [isColumnSelected, setIsColumnSelected] = useState<boolean>(false);
  const [columnPairs, setColumnPairs] = useState<Array<{ columnA: string; columnB: string }>>([]);
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [selectedMatcherColumn, setSelectedMatcherColumn] = useState<string>("");

  function getMatcherTableHeadersHandler(tableName: string) {
    getMatcherTableHeaders({
      variables: {
        tableName,
        cubeId: selectedCube.cubeId,
      },
    });
  }

  const getMatcherTableDataHandler = useCallback(
    (tableName: string) => {
      getMatcherTableData({
        variables: {
          tableName,
          cubeId: selectedCube.cubeId,
          limit: viewPageSize,
          offset: viewPage * viewPageSize,
        },
      });
    },
    [getMatcherTableData, selectedCube.cubeId, viewPageSize, viewPage]
  );

  function getViewHeadersHandler(tableName: string) {
    getViewHeaders({
      variables: {
        cubeId: selectedCube.cubeId,
        viewName: tableName,
      },
    });
  }

  const getViewDataHandler = useCallback(
    (tableName: string) => {
      getViewData({
        variables: {
          cubeId: selectedCube.cubeId,
          limit: matcherPageSize,
          offset: matcherPage * matcherPageSize,
          filter: {
            viewName: tableName,
          },
        },
      });
    },
    [getViewData, selectedCube.cubeId, matcherPageSize, matcherPage]
  );

  const currentMatcherTables = useMemo(() => {
    if (!matcherData?.matcherTables?.edges) return [];
    return matcherData.matcherTables.edges as TableData[];
  }, [matcherData]);

  const currentViewsTables = useMemo(() => {
    if (!data?.views?.edges) return [];

    return data.views.edges.filter((table) => !table.name.trim().toUpperCase().includes("_MATCH_VIEW")) as ViewsData[];
  }, [data]);

  const matcherPageData = {
    page: matcherPage,
    pageSize: matcherPageSize,
    count: matcherData?.matcherTables?.totalCount || 0,
  };

  const viewPageData = {
    page: viewPage,
    pageSize: viewPageSize,
    count: data?.views?.totalCount || 0,
  };

  const handleMatcherPageChange = (newPage: number) => {
    setMatcherPage(newPage);
  };

  const handleMatcherPageSizeChange = (newPageSize: number) => {
    setMatcherPageSize(newPageSize);
    setMatcherPage(0);
  };

  const handleViewPageChange = (newPage: number) => {
    setViewPage(newPage);
  };

  const handleViewPageSizeChange = (newPageSize: number) => {
    setViewPageSize(newPageSize);
    setViewPage(0);
  };

  const handleColumnChange = (index: number, column: string, value: string) => {
    const newPairs = [...columnPairs];
    newPairs[index] = { ...newPairs[index], [column]: value };
    setColumnPairs(newPairs);

    switch (column) {
      case "columnA": {
        setSelectedMatcherColumn(value);
        break;
      }
      case "columnB": {
        setSelectedColumn(value);
        break;
      }
    }
    if (onColumnsSelected && newPairs.every((pair) => pair.columnA && pair.columnB)) {
      onColumnsSelected(newPairs);
    }
  };

  const addColumnPair = () => {
    setColumnPairs([...columnPairs, { columnA: "", columnB: "" }]);
  };

  const removeColumnPair = (index: number) => {
    const newPairs = columnPairs.filter((_, pairIndex) => pairIndex !== index);
    setColumnPairs(newPairs);

    if (onColumnsSelected) {
      onColumnsSelected(newPairs);
    }

    setSelectedColumn("");
    setSelectedMatcherColumn("");
  };

  const onColumnsSelected = (selectedColumnPairs: Array<{ columnA: string; columnB: string }>) => {
    if (!selectedColumnPairs || selectedColumnPairs.length === 0) {
      setIsColumnSelected(false);
      return;
    }

    const columnTuples = selectedColumnPairs.map(({ columnA, columnB }) => [columnA, columnB]);
    sessionStorage.setItem("columns", JSON.stringify(columnTuples));
    setIsColumnSelected(true);
  };

  const handleSelect = (tableName: string, row: "row1" | "row2") => {
    if (selectedTables[row] === tableName) {
      setSelectedTables((prev) => ({
        ...prev,
        [row]: null,
      }));
      return;
    }
    if (row === "row1") {
      getMatcherTableHeadersHandler(tableName);
      getMatcherTableDataHandler(tableName);
    } else {
      getViewHeadersHandler(tableName);
      getViewDataHandler(tableName);
    }
    setSelectedTables((prev) => ({
      ...prev,
      [row]: tableName,
    }));
  };

  const isTableDisabled = (tableName: string, currentRow: any) => {
    return (
      (currentRow === "row1" && selectedTables.row2 === tableName) ||
      (currentRow === "row2" && selectedTables.row1 === tableName)
    );
  };

  const areTablesSelected = selectedTables.row1 && selectedTables.row2;

  const handleMatchTables = () => {
    if (!columnPairs.every((pair) => pair.columnA && pair.columnB)) {
      enqueueSnackbar("Missing pair!", { variant: "error" });
    } else {
      setPhaseData({
        ...phaseData,
        cubeId: selectedCube.cubeId,
        phase: 0,
        tableName: selectedTables.row2,
        matcherTable: selectedTables.row1,
        identifier,
        headers: columnPairs.map((pair) => [pair.columnA, pair.columnB]),
      });
      changeView(MatcherView.PREVIEW);
    }
  };

  useEffect(() => {
    setIsLoading(loading || matcherLoading);
  }, [loading, matcherLoading, setIsLoading]);

  useEffect(() => {
    if (selectedTables.row1) {
      getMatcherTableDataHandler(selectedTables.row1);
    }
  }, [getMatcherTableDataHandler, selectedTables.row1, matcherPage, matcherPageSize]);

  useEffect(() => {
    if (selectedTables.row2) {
      getViewDataHandler(selectedTables.row2);
    }
  }, [getViewDataHandler, selectedTables.row2, viewPage, viewPageSize]);

  return {
    handleSelect,
    isTableDisabled,
    matcherData,
    selectedTables,
    views: data,
    areTablesSelected,
    matcherTableData,
    identifier,
    selectedMatcherColumn,
    columnPairs,
    matcherTableHeadersData,
    setIdentifier,
    viewData,
    addColumnPair,
    handleColumnChange,
    handleMatchTables,
    isColumnSelected,
    removeColumnPair,
    selectedColumn,
    viewHeadersData,
    matcherPageData,
    handleMatcherPageChange,
    handleMatcherPageSizeChange,
    currentMatcherTables,
    viewPageData,
    handleViewPageChange,
    handleViewPageSizeChange,
    currentViewsTables,
    selectedCube,
  };
}
